import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {RefdataUtils} from "../../refdata/refdata-utils";
import {combineLatest, Observable, shareReplay, Subscription, take} from "rxjs";
import {
    BuildingType,
    BuildingTypeInfo,
    DataType,
    MeasurementsResult,
    Organisation,
    TimeRange
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {ChartDatasetExtended, MeasurementDataset} from "../base-measurement-chart";
import {ChartDataPerMeasurement, ChartDataProvider} from "../../../utils/chart-data-provider";
import {ChartData, ChartTypeRegistry, Plugin, ScaleOptionsByType} from "chart.js";
import lodash, {cloneDeep} from "lodash";
import moment from 'moment';
import {ChartOptions} from "../../dashboard/dashboard.types";
import {EnergyCompassUtils} from "./energy-compass.utils";
import {EnergyCompassLabelPlugin} from "./energy-compass-label.plugin";
import {CustomBackgroundColorPlugin} from "../scatter-chart/plugins/custom-background-color.plugin";
import {ChartAreaBorderPlugin} from "../scatter-chart/plugins/chart-area-border.plugin";
import {EnergyCompassParisProofLinePlugin} from "./energy-compass-paris-proof-line.plugin";
import {ChartModalOptions, ChartUtilsService} from "../chart-utils.service";
import {TooltipModel} from "chart.js/dist/types";
import {sendCommandAndForget, sendQuery} from "../../../common/app-common-utils";
import {View} from "../../../common/view";
import {MeasurementsHandlerOpenModalCommand} from "../../measurements-component/measurements-handler.component";
import {
    DEFAULT_WEII_CALCULATION_OPTIONS,
    summarizeWeiiCalculationOptionCoverage,
    WeiiCalculationOptionAvailability,
    WeiiCalculationOptionCoverageMap,
    WeiiCalculationOptionId,
    WeiiCalculationOptionsState,
    WeiiCalculationResult,
    WeiiCalculationService
} from "../../../utils/weii-calculation.service";
import {
    isPortfolioWeiiYearProvisional,
    latestCompletedPortfolioWeiiYear,
    normalizePortfolioWeiiYearSetting,
    portfolioWeiiAvailableYears,
    previousAvailablePortfolioWeiiYear,
    resolvePortfolioWeiiYearSelection,
    selectAutomaticPortfolioWeiiYear
} from './portfolio-weii-period.utils';
import {EnergyCompassYearComparisonPlugin} from './energy-compass-year-comparison.plugin';
import {TranslateDirective} from '../../../common/utils/translate.directive';
import {LocationWithMeasurements, mapMeasurementsToLocations} from './energy-compass-locations';
import {bestLocationEnergyLabel} from '../../common/energy-label/energy-label.component';
import {EnergyCompassTableComponent} from './energy-compass-table/energy-compass-table.component';

@Component({
    selector: 'app-energy-compass-chart',
    templateUrl: './energy-compass-chart.component.html',
    styleUrls: ['./energy-compass-chart.component.scss'],
    providers: [{provide: EnergyCompassChartComponent, useExisting: forwardRef(() => EnergyCompassChartComponent)}]
})
export class EnergyCompassChartComponent extends View implements OnInit, OnDestroy {
    private chartUtils: ChartUtilsService = inject(ChartUtilsService);
    private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private weiiCalculationService: WeiiCalculationService = inject(WeiiCalculationService);
    private selectedDashboardRange: TimeRange;
    private readonly yearlyDataByYear = new Map<number, ChartDataPerMeasurement>();
    private readonly loadingYears = new Set<number>();
    private readonly subscribedYears = new Set<number>();
    private readonly unavailableYears = new Set<number>();
    private yearlySourceFingerprint: string;
    private referenceData$: Observable<[BuildingType[], Organisation[]]>;
    private chartRenderSequence = 0;
    private portfolioEvCorrectionAvailable = false;
    private portfolioOnsiteGenerationAvailable = false;
    private portfolioClimateNormalizationAvailable = false;
    calculationOptionCoverage: WeiiCalculationOptionCoverageMap = summarizeWeiiCalculationOptionCoverage([]);
    private defaultCalculationCache = new WeakMap<LocationWithMeasurements, WeiiCalculationResult>();
    private calculationCache = new WeakMap<LocationWithMeasurements, WeiiCalculationResult>();
    private consumptionDataCache = new WeakMap<LocationWithMeasurements, boolean>();
    private buildingTypesById = new Map<string, BuildingType>();
    private activeCalculationOptions: WeiiCalculationOptionsState = DEFAULT_WEII_CALCULATION_OPTIONS;
    private calculationOptionsSubscription: Subscription;
    private readonly settingsChangedEventName = 'flowmaps:energy-compass-settings-changed';
    private readonly settingsChangedListener = (event: Event): void => {
        const detail = (event as CustomEvent<EnergyCompassSettingsChangedEvent>).detail;
        if (!detail || detail.sourceId === this.uniqueId) return;
        this.settings = this.normalizeSettings(cloneDeep(detail.settings));
        this.refreshChartInputs();
        if (this.yearlyDataByYear.size > 0) this.renderSelectedYears();
        this.changeDetectorRef.detectChanges();
    };

    @Input() showInReport: boolean;
    loading = true;

    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    @ViewChild(EnergyCompassTableComponent) energyCompassTable: EnergyCompassTableComponent;

    energyCompassLabelPlugin = new EnergyCompassLabelPlugin();

    weiiLabels: string[] = ["WENG", "Paris proof", "Very economical", "Economical", "Average", "Uneconomical", "Very uneconomical"];
    plugins: Plugin<'scatter'>[] = [new ChartAreaBorderPlugin(), new CustomBackgroundColorPlugin(),
        new EnergyCompassParisProofLinePlugin(), new EnergyCompassYearComparisonPlugin(), this.energyCompassLabelPlugin];
    gridLineColor: string = "rgba(110,110,110,0.8)";
    suggestedMinY: number = -100;
    pluginOptions = {
        chartAreaBorder: {
            borderColor: this.gridLineColor,
            borderWidth: 1,
            borderDash: 0,
            borderDashOffset: 0
        },
        customBackgroundColor: {
            color: "rgba(241,218,149,0.3)"
        }
    }
    energyCompassLayoutPadding = {top: 14, right: 0, bottom: 16, left: 0};

    modalOptions: ChartModalOptions = {
        fullScreen: false,
    }

    chartDataProvider = new ChartDataProvider();
    scatterChartData: ChartData;

    options: ChartOptions = {
        compare: {
            enabled: false
        },
        showAsTable: false
    }

    buildingTypes: BuildingType[] = [];
    plottedBuildingTypes: PlottedBuildingType[] = [];
    dotRgba: number = 0.7;
    buildingTypeColors = {
        RESTAURANT: `rgb(67,150,63,${this.dotRgba})`,
        OFFICE: `rgb(190,82,143,${this.dotRgba})`
    }

    numberOfUnknownLocations: number = 0;
    energyCompassSettingsLocalStorageKey = "energy-compass-options";
    defaultSettings: EnergyCompassSettings = {
        showUnknown: false,
        shownBuildingTypes: null,
        showAllBuildingTypes: true,
        calculationYear: null,
        compareYears: false,
        comparisonYear: null
    }
    settings: EnergyCompassSettings = this.readSettings();
    energyLabels: string[] = this.buildEnergyLabels();
    visibleBuildingTypes: string[] = [];
    scales: {[key: string]: ScaleOptionsByType<ChartTypeRegistry['scatter']['scales']>} = {};
    availableYears: number[] = [];
    automaticYear: number | null = null;
    primaryYear: number | null = null;
    activeComparisonYear: number | null = null;
    measurementTypes: DataType[] = [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak,
        DataType.electricityFeedIn, DataType.electricityFeedInOffPeak, DataType.gasConsumption,
        DataType.heatConsumption, DataType.coolingConsumption, DataType.chargePointConsumption,
        DataType.electricityGrossProduction, DataType.climateNormalizationDegreeDays];

    @Input()
    set data(d: ChartModalOptions) {
        this.modalOptions = d;
        this.energyCompassLabelPlugin.fullScreen = d?.fullScreen;
        this.energyCompassLayoutPadding = {top: 14, right: 0, bottom: d?.fullScreen ? 36 : 16, left: 0};
    }

    ngOnInit(): void {
        window.addEventListener(this.settingsChangedEventName, this.settingsChangedListener);
        if (!this.modalOptions.fullScreen) this.resetYearComparison();
        this.referenceData$ = combineLatest([
            sendQuery('findBuildingTypes'),
            this.subscribeTo('getOrganisations')
        ]).pipe(take(1), shareReplay({bufferSize: 1, refCount: true}));
        this.loadYear(latestCompletedPortfolioWeiiYear());
        this.calculationOptionsSubscription = this.weiiCalculationService.options$.subscribe(() => {
            if (this.yearlyDataByYear.size > 0) this.renderSelectedYears();
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener(this.settingsChangedEventName, this.settingsChangedListener);
        this.calculationOptionsSubscription?.unsubscribe();
        this.onDestroy();
    }

    private uniqueId = lodash.uniqueId();
    getInputId = (id: string) => this.uniqueId + id + (this.modalOptions.fullScreen ? '-fullscreen' : '');

    saveSettings() {
        this.settings = this.normalizeSettings(this.settings);
        localStorage.setItem(this.energyCompassSettingsLocalStorageKey, JSON.stringify(this.settings));
        window.dispatchEvent(new CustomEvent<EnergyCompassSettingsChangedEvent>(this.settingsChangedEventName, {
            detail: {sourceId: this.uniqueId, settings: cloneDeep(this.settings)}
        }));
    }

    private readSettings(): EnergyCompassSettings {
        let storedSettings: Partial<EnergyCompassSettings> = {};
        try {
            storedSettings = JSON.parse(localStorage.getItem(this.energyCompassSettingsLocalStorageKey)) || {};
        } catch {
            storedSettings = {};
        }
        const settings = this.normalizeSettings(storedSettings);
        localStorage.setItem(this.energyCompassSettingsLocalStorageKey, JSON.stringify(settings));
        return settings;
    }

    private normalizeSettings(settings: Partial<EnergyCompassSettings>): EnergyCompassSettings {
        const merged = {...this.defaultSettings, ...(settings || {})};
        return {
            ...merged,
            calculationYear: normalizePortfolioWeiiYearSetting(merged.calculationYear),
            comparisonYear: normalizePortfolioWeiiYearSetting(merged.comparisonYear)
        };
    }

    private buildScales() {
        const scales: {
            [key: string]: ScaleOptionsByType<ChartTypeRegistry['scatter']['scales']>;
        } = {};
        this.buildingTypes.forEach(b => {
            const scaleTicks = this.getScaleTicks(b);
            scales[b.info.code] = (<ScaleOptionsByType<ChartTypeRegistry['scatter']['scales']>>{
                type: "linear",
                display: this.visibleBuildingTypes.length === 1 && this.visibleBuildingTypes.includes(b.buildingTypeId),
                title: {
                    display: true,
                    text: 'kWh/m²'
                },
                position: "right",
                min: 0,
                suggestedMin: 0,
                max: scaleTicks.length - 1,
                suggestedMax: scaleTicks.length - 1,
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkip: false,
                    count: scaleTicks.length,
                    stepSize: 1,
                    callback: (tickValue, index, ticks) => index === scaleTicks.length - 1
                        ? `> ${this.chartUtils.transformDecimal(scaleTicks[index])}` : this.chartUtils.transformDecimal(scaleTicks[index])
                },
                beginAtZero: false
            });
            scales[b.info.code]["realTicks"] = scaleTicks;
            return scales[b.info.code];
        });
        return scales;
    }

    private getScaleTicks = (b: BuildingType) => Object.values(b.info.efficiencyScale)
        .concat([this.suggestedMinY, b.info.efficiencyScale["Uneconomical"] * 2])
        .sort((a, b) => a < b ? -1 : 1);

    private loadYear(year: number): void {
        if (!Number.isInteger(year) || this.yearlyDataByYear.has(year)
            || this.loadingYears.has(year) || this.unavailableYears.has(year)) return;
        this.loading = true;
        this.loadingYears.add(year);
        if (this.subscribedYears.has(year)) return;
        this.subscribedYears.add(year);
        this.subscribeTo('getEnergyCompassMeasurements', {year})
            .subscribe((data: ChartDataPerMeasurement) => this.loadPortfolioYear(year, data));
    }

    private loadPortfolioYear(year: number, d: ChartDataPerMeasurement): void {
        this.selectedDashboardRange = d?.timeRange;
        const sourceFingerprint = d?.queryFingerprint || JSON.stringify({
            timeRange: d?.timeRange,
            dashboardType: d?.dashboardType,
            entityType: d?.entityType
        });
        if (this.yearlySourceFingerprint != null && sourceFingerprint !== this.yearlySourceFingerprint) {
            this.resetYearComparison();
            this.yearlyDataByYear.clear();
            this.loadingYears.clear();
            this.unavailableYears.clear();
        }
        if (sourceFingerprint !== this.yearlySourceFingerprint) {
            this.availableYears = portfolioWeiiAvailableYears(d?.timeRange, moment().year());
        }
        this.yearlySourceFingerprint = sourceFingerprint;
        this.loadingYears.delete(year);
        if (this.extractAvailableYears(d).includes(year)) {
            this.unavailableYears.delete(year);
            this.yearlyDataByYear.set(year, d);
        } else {
            this.yearlyDataByYear.delete(year);
            this.unavailableYears.add(year);
            this.availableYears = this.availableYears.filter(availableYear => availableYear !== year);
        }
        this.renderSelectedYears();
    }

    private renderSelectedYears(): void {
        if (this.availableYears.length === 0) {
            this.loading = false;
            return;
        }
        this.energyCompassLabelPlugin.showUnknown = this.settings.showUnknown;
        this.automaticYear = selectAutomaticPortfolioWeiiYear(
            this.availableYears, this.selectedDashboardRange, moment().year());
        const selection = resolvePortfolioWeiiYearSelection(this.availableYears, this.automaticYear,
            this.settings.calculationYear, this.settings.compareYears, this.settings.comparisonYear);
        this.primaryYear = selection.primaryYear;
        this.activeComparisonYear = selection.comparisonYear;
        if (selection.primaryYear != null && !this.yearlyDataByYear.has(selection.primaryYear)) {
            this.loadYear(selection.primaryYear);
            return;
        }
        if (selection.comparisonYear != null && !this.yearlyDataByYear.has(selection.comparisonYear)) {
            this.loadYear(selection.comparisonYear);
            return;
        }
        this.loading = false;
        const renderSequence = ++this.chartRenderSequence;
        const primaryData = this.filterDataForYear(this.yearlyDataByYear.get(selection.primaryYear), selection.primaryYear);
        const comparisonData = selection.comparisonYear == null ? null
            : this.filterDataForYear(this.yearlyDataByYear.get(selection.comparisonYear), selection.comparisonYear);
        this.updateChart(primaryData, selection.primaryYear, comparisonData, selection.comparisonYear, renderSequence);
    }

    private extractAvailableYears(data: ChartDataPerMeasurement): number[] {
        const years = (data?.byEntity || []).flatMap(result =>
            [result.measurements || {}, result.estimatedMeasurements || {}].flatMap(measurements =>
                Object.entries(measurements)
                    .filter(([dataType]) => this.measurementTypes.includes(dataType as DataType)
                        && dataType !== DataType.climateNormalizationDegreeDays)
                    .flatMap(([, points]) => (points || []).map(point => moment(point.timeRange.start).year()))));
        return [...new Set(years)].sort((a, b) => b - a);
    }

    private filterDataForYear(data: ChartDataPerMeasurement | undefined, year: number | null): ChartDataPerMeasurement {
        if (!data || year == null) return {...(data || {}), byEntity: []};
        return {
            ...data,
            byEntity: (data.byEntity || []).map(result => ({
                ...result,
                measurements: this.filterMeasurementMapForYear(result.measurements, year),
                estimatedMeasurements: this.filterMeasurementMapForYear(result.estimatedMeasurements, year)
            }))
        };
    }

    private filterMeasurementMapForYear(measurements: MeasurementsResult['measurements'], year: number) {
        return lodash.pickBy(lodash.mapValues(measurements || {}, points =>
            (points || []).filter(point => moment(point.timeRange.start).year() === year)), points => points.length > 0);
    }

    private updateChart(primaryData: ChartDataPerMeasurement, primaryYear: number | null,
                        comparisonData: ChartDataPerMeasurement | null, comparisonYear: number | null,
                        renderSequence: number): void {
        this.referenceData$.subscribe(([buildingTypes, organisations]) => {
            if (renderSequence !== this.chartRenderSequence) return;
            this.buildingTypes = buildingTypes;
            this.buildingTypesById = new Map(this.buildingTypes.map(type => [type.buildingTypeId, type]));
            this.defaultCalculationCache = new WeakMap<LocationWithMeasurements, WeiiCalculationResult>();
            this.calculationCache = new WeakMap<LocationWithMeasurements, WeiiCalculationResult>();
            this.consumptionDataCache = new WeakMap<LocationWithMeasurements, boolean>();
            const primaryLocations = mapMeasurementsToLocations(organisations, primaryData?.byEntity || []);
            const comparisonLocations = comparisonData
                ? mapMeasurementsToLocations(organisations, comparisonData.byEntity || []) : [];
            const calculationLocations = primaryLocations
                .filter(location => this.settings.showUnknown || bestLocationEnergyLabel(location.location))
                .filter(location => this.hasConsumptionData(location) && RefdataUtils.getLocationArea(location.location));
            this.calculationOptionCoverage = summarizeWeiiCalculationOptionCoverage(
                calculationLocations.map(location => this.getDefaultCalculation(location)));
            this.portfolioEvCorrectionAvailable = this.calculationOptionCoverage[
                WeiiCalculationOptionId.ExcludeEvCharging].available > 0;
            this.portfolioOnsiteGenerationAvailable = this.calculationOptionCoverage[
                WeiiCalculationOptionId.IncludeOnsiteGeneration].available > 0;
            this.portfolioClimateNormalizationAvailable = this.calculationOptionCoverage[
                WeiiCalculationOptionId.NormalizeClimateYear].available > 0;
            this.activeCalculationOptions = this.effectiveCalculationOptions;

            const primaryGroups: lodash.Dictionary<LocationWithMeasurements[]> = lodash.groupBy(
                primaryLocations, location => location.location.info.buildingType);
            const comparisonGroups: lodash.Dictionary<LocationWithMeasurements[]> = lodash.groupBy(
                comparisonLocations, location => location.location.info.buildingType);
            const uniqueLocations = lodash.uniqBy([...primaryLocations, ...comparisonLocations], location => location.location.locationId);
            const allGroups: lodash.Dictionary<LocationWithMeasurements[]> = lodash.groupBy(
                uniqueLocations, location => location.location.info.buildingType);
            this.plottedBuildingTypes = cloneDeep(this.buildingTypes)
                .map(buildingType => ({
                    ...buildingType,
                    numberOfLocations: allGroups[buildingType.buildingTypeId]?.length || 0
                }))
                .filter(buildingType => buildingType.numberOfLocations > 0);
            if (!this.settings.shownBuildingTypes) {
                this.settings.shownBuildingTypes = {};
                this.plottedBuildingTypes.forEach(type =>
                    this.settings.shownBuildingTypes[type.buildingTypeId] = type.info);
            }
            this.numberOfUnknownLocations = uniqueLocations
                .filter(location => !bestLocationEnergyLabel(location.location)).length;
            this.refreshChartInputs();

            const primaryDatasets = primaryYear == null ? [] : this.datasetsForGroups(primaryGroups, primaryYear, false);
            const comparisonDatasets = comparisonYear == null ? []
                : this.datasetsForGroups(comparisonGroups, comparisonYear, true);
            this.scatterChartData = {datasets: [...primaryDatasets, ...comparisonDatasets]};
            this.emitData();
            this.changeDetectorRef.detectChanges();
        });
    }

    private datasetsForGroups(groups: lodash.Dictionary<LocationWithMeasurements[]>, year: number,
                              comparison: boolean): ChartDatasetExtended[] {
        return Object.entries(groups)
            .map(([buildingTypeId, values]) => ({
                buildingType: this.plottedBuildingTypes.find(type => type.buildingTypeId === buildingTypeId),
                values
            }))
            .filter(group => !!group.buildingType)
            .map(group => this.datasetForLocations(group.buildingType, group.values, year, comparison));
    }

    private emitData() {
        this.chartDataProvider.emit({
            datasets: this.scatterChartData.datasets.map(d => <MeasurementDataset>{
                dataset: d
            })
        });
    }

    toggleShowAsTable() {
        this.options.showAsTable = !this.options.showAsTable;
        setTimeout(() => this.chartDataProvider.emit({
            datasets: this.scatterChartData.datasets.map(d => <MeasurementDataset>{
                dataset: d
            })
        }), 0);
    }

    downloadTable = (): void => this.energyCompassTable?.downloadTable(
        'weii-energiekompas.xlsx', 'WEii Energiekompas – volledige berekening');

    openModal() {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: EnergyCompassChartComponent,
            chartOptions: this.options,
            chartModalData: this.modalOptions,
            container: this.container,
            modalClasses: "modal-dialog-centered modal-xl d-flex align-items-stretch",
            maxModalScale: 1.35,
            modalHeightScale: 1.55
        });
    }

    closeModal = () => sendCommandAndForget("closeModal");

    private datasetForLocations(buildingType: BuildingType, locations: LocationWithMeasurements[], year: number,
                                comparison: boolean): ChartDatasetExtended {
        const color = this.buildingTypeColors[buildingType.info.code] || this.stringToRgba(buildingType.info.code);
        const locationWithMeasurements = locations.filter(l => this.settings.showUnknown || bestLocationEnergyLabel(l.location))
            .filter(l => this.hasConsumptionData(l) && RefdataUtils.getLocationArea(l.location));
        return <ChartDatasetExtended>{
            pointRadius: comparison ? 5 : 4,
            pointHoverRadius: comparison ? 6 : 5,
            backgroundColor: comparison ? 'rgba(255,255,255,0.94)' : color,
            pointBorderColor: color,
            pointBorderWidth: comparison ? 2 : 0,
            yAxisID: buildingType.info.code,
            indexAxis: "y",
            hidden: !this.isBuildingTypeShown(buildingType.buildingTypeId),
            comparisonYear: comparison,
            calculationYear: year,
            data: locationWithMeasurements
                .map(l => {
                    const name = RefdataUtils.locationsFormatter(l.location);
                    return ({
                        x: this.energyLabels.indexOf(bestLocationEnergyLabel(l.location) || EnergyCompassParisProofLinePlugin.unknownLabel),
                        y: this.getValueForScale(l),
                        label: name,
                        location: l.location,
                        locationId: l.location.locationId,
                        organisationId: l.organisationId,
                        calculationYear: year,
                        comparisonYear: comparison,
                        weiiResult: this.getCalculation(l)
                    });
                }),
            buildingType: buildingType,
            tooltip: {
                formatter: this.energyCompassTooltipFormatter(year,
                    locationWithMeasurements.map(location => this.getCalculation(location))),
                data: locationWithMeasurements.map(l => lodash.round(this.getKwhPerSquareMeter(l), 1))
            }
        };
    }

    hasConsumptionData = (loc: LocationWithMeasurements): boolean => {
        const cached = this.consumptionDataCache.get(loc);
        if (cached !== undefined) return cached;
        const hasData = loc.measurements.flatMap(m => Object.entries(m.measurements)
            .filter(([dataType, points]) => this.measurementTypes.includes(dataType as DataType)
                && dataType !== DataType.climateNormalizationDegreeDays && points?.length > 0)).length > 0;
        this.consumptionDataCache.set(loc, hasData);
        return hasData;
    }

    getKwhPerSquareMeter = (loc: LocationWithMeasurements): number => {
        return this.getCalculation(loc).effectiveWeii ?? 0;
    }

    getCalculation = (loc: LocationWithMeasurements): WeiiCalculationResult => {
        if (Object.values(this.activeCalculationOptions).every(value => !value)) {
            return this.getDefaultCalculation(loc);
        }
        let calculation = this.calculationCache.get(loc);
        if (!calculation) {
            calculation = this.calculateLocation(loc, this.activeCalculationOptions);
            this.calculationCache.set(loc, calculation);
        }
        return calculation;
    }

    private getDefaultCalculation(loc: LocationWithMeasurements): WeiiCalculationResult {
        let calculation = this.defaultCalculationCache.get(loc);
        if (!calculation) {
            calculation = this.calculateLocation(loc, DEFAULT_WEII_CALCULATION_OPTIONS);
            this.defaultCalculationCache.set(loc, calculation);
        }
        return calculation;
    }

    private calculateLocation(loc: LocationWithMeasurements,
                              calculationOptions: WeiiCalculationOptionsState): WeiiCalculationResult {
        const buildingType = this.buildingTypesById.get(loc.location.info.buildingType);
        return this.weiiCalculationService.calculateMeasurements({
            measurements: loc.measurements,
            floorAreaM2: RefdataUtils.getLocationArea(loc.location),
            isFullElectric: loc.location.info?.allElectric === true,
            calculationOptions,
            buildingCategory: buildingType?.info?.name
        });
    }

    get applyEvCorrection(): boolean {
        return this.effectiveCalculationOptions[WeiiCalculationOptionId.ExcludeEvCharging];
    }

    get evCorrectionAvailable(): boolean {
        return this.portfolioEvCorrectionAvailable;
    }

    get calculationOptionAvailability(): WeiiCalculationOptionAvailability {
        return {
            [WeiiCalculationOptionId.ExcludeEvCharging]: this.portfolioEvCorrectionAvailable,
            [WeiiCalculationOptionId.IncludeOnsiteGeneration]: this.portfolioOnsiteGenerationAvailable,
            [WeiiCalculationOptionId.NormalizeClimateYear]: this.portfolioClimateNormalizationAvailable
        };
    }

    private get effectiveCalculationOptions(): WeiiCalculationOptionsState {
        return {
            ...this.weiiCalculationService.options,
            [WeiiCalculationOptionId.ExcludeEvCharging]:
                this.weiiCalculationService.isOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging)
                && this.portfolioEvCorrectionAvailable,
            [WeiiCalculationOptionId.IncludeOnsiteGeneration]:
                this.weiiCalculationService.isOptionEnabled(WeiiCalculationOptionId.IncludeOnsiteGeneration)
                && this.portfolioOnsiteGenerationAvailable,
            [WeiiCalculationOptionId.NormalizeClimateYear]:
                this.weiiCalculationService.isOptionEnabled(WeiiCalculationOptionId.NormalizeClimateYear)
                && this.portfolioClimateNormalizationAvailable
        };
    }

    setApplyEvCorrection(value: boolean): void {
        if (!value || this.evCorrectionAvailable) {
            this.weiiCalculationService.setOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging, value);
        }
    }

    getValueForScale = (loc: LocationWithMeasurements): number => {
        const valuePerSquareMeter = this.getKwhPerSquareMeter(loc);
        const buildingType = this.buildingTypesById.get(loc.location.info.buildingType);
        const ticks = this.getScaleTicks(buildingType);
        let valueFound = false;
        return ticks.reduce((a, b, currentIndex) => {
            if (valuePerSquareMeter > b) {
                if (currentIndex === ticks.length - 1) {
                    return currentIndex;
                }
                return b;
            }
            if (valueFound) {
                return a;
            }
            valueFound = true;
            const relativePosition = this.calculateRelativePosition(valuePerSquareMeter, a, b);
            return currentIndex - 1 + relativePosition;
        });
    }

    private calculateRelativePosition = (value, min, max) => (value - min) / (max - min);

    private energyCompassTooltipFormatter(year: number, calculations: WeiiCalculationResult[]):
        (ctx: TooltipModel<any>, value: number, index: number) => string {
        return (ctx, value, index) => {
            const calculation = calculations[index];
            const optionLabels = [
                this.optionTooltipLabel(calculation, WeiiCalculationOptionId.IncludeOnsiteGeneration,
                    'onsite generation included', 'onsite generation not applied'),
                this.optionTooltipLabel(calculation, WeiiCalculationOptionId.ExcludeEvCharging,
                    'EV charging excluded', 'EV charging exclusion not applied'),
                this.optionTooltipLabel(calculation, WeiiCalculationOptionId.NormalizeClimateYear,
                    'climate normalized', 'climate normalization not applied')
            ].filter(Boolean);
            return `${year}: ${this.chartUtils.getNumberTooltipFormatter()(ctx, value)} kWh/m² `
                + (optionLabels.length > 0 ? `(${optionLabels.join(', ')})`
                    : `(${TranslateDirective.getTranslation('WEii indicative', true)})`);
        };
    }

    private optionTooltipLabel(calculation: WeiiCalculationResult | undefined, optionId: WeiiCalculationOptionId,
                               appliedLabel: string, unavailableLabel: string): string | null {
        if (!this.effectiveCalculationOptions[optionId]) return null;
        return TranslateDirective.getTranslation(
            calculation?.appliedOptions.includes(optionId) ? appliedLabel : unavailableLabel, true);
    }

    stringToRgba(str: string, alpha = 1): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
            hash = hash & hash;
        }
        let rgb = [0, 0, 0];
        for (let i = 0; i < rgb.length; i++) {
            rgb[i] = (hash >> (i * 8)) & 255;
        }
        return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
    }

    togglePlottedBuildingType(buildingType: BuildingType) {
        const isShown: boolean = !!this.settings.shownBuildingTypes[buildingType.buildingTypeId];
        if (isShown) {
            delete this.settings.shownBuildingTypes[buildingType.buildingTypeId];
        } else {
            this.settings.shownBuildingTypes[buildingType.buildingTypeId] = buildingType.info;
        }
        this.refreshChartInputs();
        this.scatterChartData.datasets.filter(d => d["buildingType"])
            .forEach(d => {
                d.hidden = !this.isBuildingTypeShown(d["buildingType"].buildingTypeId);
            });
        this.saveSettings();
        this.emitData();
    }

    toggleShowAllBuildingTypes(): void {
        this.settings.showAllBuildingTypes = !this.settings.showAllBuildingTypes;
        if (!this.settings.showAllBuildingTypes && Object.keys(this.settings.shownBuildingTypes || {}).length === 0) {
            this.plottedBuildingTypes.forEach(type =>
                this.settings.shownBuildingTypes[type.buildingTypeId] = type.info);
        }
        this.refreshChartInputs();
        this.scatterChartData.datasets.filter(dataset => dataset['buildingType']).forEach(dataset =>
            dataset.hidden = !this.isBuildingTypeShown(dataset['buildingType'].buildingTypeId));
        this.saveSettings();
        this.emitData();
    }

    private isBuildingTypeShown(buildingTypeId: string): boolean {
        return this.settings.showAllBuildingTypes || !!this.settings.shownBuildingTypes?.[buildingTypeId];
    }

    setCalculationYear(value: string | number | null): void {
        this.settings.calculationYear = value == null || value === 'automatic' ? null : Number(value);
        this.saveSettings();
        this.renderSelectedYears();
    }

    toggleCompareYears(): void {
        this.settings.compareYears = !this.settings.compareYears;
        if (this.settings.compareYears && this.settings.comparisonYear == null && this.primaryYear != null) {
            this.settings.comparisonYear = previousAvailablePortfolioWeiiYear(this.availableYears, this.primaryYear);
        }
        this.saveSettings();
        this.renderSelectedYears();
    }

    setComparisonYear(value: number): void {
        this.settings.comparisonYear = Number(value);
        this.settings.compareYears = true;
        this.saveSettings();
        this.renderSelectedYears();
    }

    yearIsProvisional(year: number): boolean {
        return isPortfolioWeiiYearProvisional(year);
    }

    yearOptionLabel(year: number): string {
        const provisional = this.yearIsProvisional(year)
            ? ` - ${TranslateDirective.getTranslation('Provisional', true)}` : '';
        return `${year}${provisional}`;
    }

    get comparisonYearSelection(): number | null {
        if (this.settings.comparisonYear != null
            && this.settings.comparisonYear !== this.primaryYear
            && this.availableYears.includes(this.settings.comparisonYear)) {
            return this.settings.comparisonYear;
        }
        return previousAvailablePortfolioWeiiYear(this.availableYears, this.primaryYear);
    }

    get automaticYearOptionLabel(): string {
        return `${TranslateDirective.getTranslation('Automatic', true)} (${this.automaticYear})`;
    }

    get calculationYearPinned(): boolean {
        return this.settings.calculationYear != null && this.settings.calculationYear === this.primaryYear;
    }

    get dominantYearColor(): string {
        const visiblePrimaryDatasets = (this.scatterChartData?.datasets || [])
            .filter(dataset => !dataset.hidden && dataset['comparisonYear'] !== true);
        const dominantDataset = lodash.maxBy(visiblePrimaryDatasets, dataset => dataset.data?.length || 0);
        const color = dominantDataset?.['pointBorderColor'] || dominantDataset?.backgroundColor;
        return typeof color === 'string' ? color : 'var(--bs-primary)';
    }

    private buildEnergyLabels(): string[] {
        return EnergyCompassUtils.labels.map(e => e as string)
            .concat(this.settings.showUnknown ? [EnergyCompassParisProofLinePlugin.unknownLabel] : []);
    }

    private buildVisibleBuildingTypes(): string[] {
        return this.settings.showAllBuildingTypes
            ? this.plottedBuildingTypes.map(type => type.buildingTypeId)
            : Object.keys(this.settings.shownBuildingTypes || {})
                .filter(id => this.plottedBuildingTypes.some(type => type.buildingTypeId === id));
    }

    private refreshChartInputs(): void {
        this.energyLabels = this.buildEnergyLabels();
        this.visibleBuildingTypes = this.buildVisibleBuildingTypes();
        this.scales = this.buildScales();
    }

    toggleShowUnknown = () => {
        this.settings.showUnknown = !this.settings.showUnknown;
        this.refreshChartInputs();
        this.saveSettings();
        this.renderSelectedYears();
    }

    private resetYearComparison(): void {
        if (!this.settings.compareYears && this.settings.comparisonYear == null) return;
        this.settings.compareYears = false;
        this.settings.comparisonYear = null;
        this.saveSettings();
    }
}

interface PlottedBuildingType extends BuildingType {
    numberOfLocations?: number;
}

export interface EnergyCompassSettings {
    showUnknown: boolean,
    shownBuildingTypes: lodash.Dictionary<BuildingTypeInfo>;
    showAllBuildingTypes: boolean;
    calculationYear: number | null;
    compareYears: boolean;
    comparisonYear: number | null;
}

interface EnergyCompassSettingsChangedEvent {
    sourceId: string;
    settings: EnergyCompassSettings;
}
